import { useState, useEffect, useMemo, useRef } from 'react'
import {
  Clock,
  Search,
  SendHorizontal,
  RefreshCcw,
  CalendarSync,
  FileBox,
  Plus,
} from 'lucide-react'
import Navbar from '../../components/navbar/navbar'
import apinode from '../../services/apinode'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ContentLoading } from '../../components/ContentLoading'
import { Modal, OverlayTrigger, ProgressBar, Tooltip } from 'react-bootstrap'
import { CustomSpinner } from '../../components/CustomSpinner'
import { toast } from 'react-toastify'
import '../pedido-editar/pedido-editar.css'
import './mercado-livre-messages.css'
import { formatDate } from '../../utils/formatDate'
import { Ask } from '../../components/Ask'
import { FilePdf } from 'phosphor-react'
import { FileTxt } from '@phosphor-icons/react'

const locale = ptBR

const ACCOUNT_PALETTE = [
  { bg: '#EFEDFF', text: '#4F3FE0', border: '#D9D4FF' },
  { bg: '#FFF1E9', text: '#C2540B', border: '#FFD9BE' },
  { bg: '#EAFBF3', text: '#0F8A5F', border: '#BEEED9' },
  { bg: '#FDEEF6', text: '#B32D77', border: '#F9CFE6' },
  { bg: '#EAF3FF', text: '#1D5FD6', border: '#C6DEFF' },
]

export default function MercadoLivreMessages() {
  // --- ESTADOS ---
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncLoding, setIsSyncLoading] = useState(false)
  const [isAllSyncLoding, setIsAllSyncLoading] = useState(false)
  const [response, setResponse] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [contas, setContas] = useState([])
  const [selectedConta, setSelectedConta] = useState(localStorage.idconta)
  const [isMessagesLoading, setIsMessagesLoading] = useState(false)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [selectedAttachment, setSelectedAttachment] = useState(null)
  const [synced, setSynced] = useState({
    finished: 0,
    success: 0,
    error: 0,
  })

  // --- REFS ---
  const messagesEndRef = useRef(null)
  const inputFileRef = useRef(null)
  const iframeRef = useRef(null)

  // --- HELPER FUNCTIONS INTERNAS ---
  function getFileExtension(url) {
    if (!url) return ''
    const clean = url.split('?')[0].split('#')[0]
    const match = clean.match(/\.([a-zA-Z0-9]+)$/)
    return match ? match[1].toLowerCase() : ''
  }

  function selectAttachment(e) {
    const file = e.target.files?.[0]

    if (file) {
      setSelectedAttachment(file)

      iframeRef.current?.setAttribute('src', URL.createObjectURL(file))
    }
  }

  function isImageUrl(url) {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(
      getFileExtension(url),
    )
  }

  function getFileNameFromUrl(url) {
    if (!url) return 'arquivo'
    try {
      const clean = url.split('?')[0].split('#')[0]
      const parts = clean.split('/')
      return decodeURIComponent(parts[parts.length - 1]) || 'arquivo'
    } catch {
      return 'arquivo'
    }
  }

  async function downloadFile(url, filename) {
    try {
      const resp = await fetch(url)
      const blob = await resp.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error)
      window.open(url, '_blank')
    }
  }

  function parseServerDate(value) {
    return new Date(value)
  }

  function hashToIndex(str, mod) {
    const s = String(str || '')
    let h = 0
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
    return h % mod
  }

  function accountPalette(label) {
    return ACCOUNT_PALETTE[hashToIndex(label, ACCOUNT_PALETTE.length)]
  }

  function initialsOf(name) {
    const clean = String(name || '').trim()
    if (!clean) return '?'
    const parts = clean.split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // --- REQUISIÇÕES E AÇÕES ---
  async function fetchConversations() {
    try {
      setIsLoading(true)
      const resp = await apinode.get(
        `/ml/messages/${localStorage.idconta}?seller_id=${selectedConta}`,
      )
      const data = resp.data?.conversas || resp.data || []
      setConversations(data)

      if (data.length > 0 && !selectedConversation?.pack_id) {
        setSelectedConversation(data[0])
      }
    } catch (error) {
      console.error('Erro ao buscar lista de conversas:', error)
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchPackMessages(packId) {
    if (!packId) return

    setIsMessagesLoading(true)

    try {
      const resp = await apinode.get(
        `/ml/messages/${packId}/${localStorage.idconta}`,
      )

      if (resp.data?.length > 0) {
        setSelectedConversation((prev) => ({
          ...prev,
          id_pedido: resp.data[0].id_pedido,
          messages: resp.data,
        }))
      }
    } catch (err) {
      console.error('Erro ao buscar histórico do pack:', err)
    } finally {
      setIsMessagesLoading(false)
    }
  }

  async function syncMessages(pack_id, disable_feedback) {
    try {
      setIsSyncLoading(true)

      await apinode.get(
        `/ml/messages/sync/${localStorage.idconta}?pack_id=${pack_id}&seller_id=${selectedConta}`,
      )

      await fetchPackMessages(pack_id)

      setSynced((pv) => ({
        ...pv,
        success: pv.success + 1,
      }))

      if (!disable_feedback) {
        toast.success('Mensagens sincronizadas com sucesso!')
      }
    } catch (error) {
      setSynced((pv) => ({
        ...pv,
        error: pv.error + 1,
      }))

      if (!disable_feedback) {
        toast.error('Erro ao sincronizar mensagens:', error)
      }
    } finally {
      setSynced((pv) => ({
        ...pv,
        finished: pv.finished + 1,
      }))
      setIsSyncLoading(false)
    }
  }

  async function syncAllMessages() {
    try {
      setIsAllSyncLoading(true)

      for (let c = 0; c <= conversations.length; c += 10) {
        await Promise.allSettled(
          conversations
            .slice(c, c + 10)
            .map((conversation) => syncMessages(conversation.pack_id, true)),
        )
      }

      await fetchConversations()
      toast.success('Todas as mensagens foram sincronizadas com sucesso!')
    } catch (error) {
      toast.error('Erro ao sincronizar todas mensagens:', error)
    } finally {
      setIsAllSyncLoading(false)
    }
  }

  const handleSendMessage = async () => {
    try {
      const formData = new FormData()
      formData.append('pack_id', selectedConversation?.pack_id || '')
      formData.append('seller_id', String(selectedConta))
      formData.append(
        'to_user_id',
        selectedConversation?.ultimo_remetente || '',
      )
      formData.append('text', response)

      if (inputFileRef.current?.files?.[0]) {
        formData.append('file', inputFileRef.current.files[0])
      }

      const resp = await apinode.post('/ml/messages/send', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      toast.success('Mensagem enviada!')
      setResponse('')
      setSelectedAttachment(null)
      if (inputFileRef.current) inputFileRef.current.value = ''

      if (selectedConversation?.pack_id) {
        fetchPackMessages(selectedConversation.pack_id)
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error)
      toast.error('Erro ao enviar mensagem')
    }
  }

  // --- MEMOS ---
  const filteredConversations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) {
      return conversations.sort(
        (a, b) =>
          parseServerDate(b.data_ultima_mensagem) -
          parseServerDate(a.data_ultima_mensagem),
      )
    }

    return conversations.filter(
      (c) =>
        c.nome_comprador?.toLowerCase().includes(q) ||
        c.pack_id?.toLowerCase().includes(q) ||
        c.ultima_mensagem?.toLowerCase().includes(q) ||
        c.ultimo_remetente?.toLowerCase().includes(q),
    )
  }, [conversations, searchTerm])

  const activePalette = useMemo(() => {
    return accountPalette(
      selectedConversation?.seller_id || localStorage.idconta,
    )
  }, [selectedConversation?.seller_id])

  const canSend = Boolean(response?.trim())

  const progress = (synced.finished / conversations.length) * 100

  // --- EFFECTS ---
  useEffect(() => {
    if (selectedConversation?.pack_id) {
      fetchPackMessages(selectedConversation?.pack_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.pack_id])

  useEffect(() => {
    fetchConversations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConta])

  useEffect(() => {
    ;(async () => {
      try {
        const res = await apinode.get(`/accounts/${localStorage.idconta}`)
        setContas(res.data)
      } catch (err) {
        console.error('Erro ao carregar contas:', err)
      }
    })()
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [selectedConversation?.messages])

  return (
    <>
      <Navbar />

      <div className="ml-messages">
        <ContentLoading isLoading={isLoading}>
          <div className="ml-messages__page">
            {/* ================= BARRA LATERAL (SIDEBAR) ================= */}
            <div className="ml-card ml-sidebar">
              {/* Header da Sidebar */}
              <div className="ml-sidebar__header">
                <div>
                  <h3 className="ml-sidebar__title">Mensagens</h3>
                  <div className="ml-sidebar__subtitle">
                    {filteredConversations.length} conversas
                  </div>

                  <OverlayTrigger
                    overlay={<Tooltip>Sincronizar todas as mensagens</Tooltip>}
                  >
                    <button
                      className="ml-refresh-btn"
                      onClick={() =>
                        Ask(
                          'Esse processo é longo e pode demorar alguns minutos. Continuar?',
                          () => syncAllMessages(),
                        )
                      }
                      disabled={isAllSyncLoding}
                    >
                      {isAllSyncLoding ? (
                        <CustomSpinner
                          size={16}
                          color="white"
                        />
                      ) : (
                        <CalendarSync size={16} />
                      )}
                    </button>
                  </OverlayTrigger>
                </div>

                <div className="ml-account-info">
                  <div className="ml-account-info__accent">
                    <label htmlFor="account-select">Conta</label>
                    <select
                      id="account-select"
                      name="account-select"
                      value={selectedConta}
                      onChange={(e) => {
                        setSelectedConta(e.target.value)
                        setSelectedConversation(null)
                      }}
                    >
                      {contas.map((conta) => (
                        <option
                          key={conta.id_conta}
                          value={conta.id_conta}
                        >
                          {conta.apelido}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Busca da Sidebar */}
              <div className="ml-search">
                <div className="ml-search__input-wrap">
                  <Search
                    size={16}
                    className="ml-search__icon"
                  />
                  <input
                    type="text"
                    placeholder="Buscar por comprador ou texto..."
                    className="ml-search__input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Lista de Conversas */}
              <div className="ml-chat-list">
                {filteredConversations.map((conversation) => {
                  const palette = accountPalette(conversation.seller_id)
                  const isSelected =
                    selectedConversation?.pack_id === conversation.pack_id

                  return (
                    <div
                      key={conversation.pack_id}
                      className={`ml-chat-item${
                        isSelected ? ' ml-chat-item--active' : ''
                      }`}
                      onClick={() => setSelectedConversation(conversation)}
                    >
                      {/* Avatar Inline */}
                      <div
                        className="ml-avatar"
                        style={{
                          background: palette.bg,
                          color: palette.text,
                          borderColor: palette.border,
                        }}
                      >
                        {initialsOf(
                          conversation.nome_comprador || conversation.pack_id,
                        )}
                      </div>

                      <div className="ml-chat-item__body">
                        <div className="ml-chat-item__title-row">
                          <div className="ml-chat-item__title">
                            {conversation.nome_comprador}
                          </div>
                          <div className="ml-chat-item__time">
                            {conversation.data_ultima_mensagem
                              ? formatDistanceToNow(
                                  parseServerDate(
                                    conversation.data_ultima_mensagem,
                                  ),
                                  { locale },
                                )
                              : ''}
                          </div>
                        </div>

                        <div className="ml-chat-item__last-msg">
                          {conversation.tem_anexo === 'S' ? (
                            <span className="ml-chat-item__attachment">
                              <FileBox size={12.5} />
                              Arquivo
                            </span>
                          ) : (
                            conversation.ultima_mensagem
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* ================= PAINEL DO CHAT ================= */}
            <ContentLoading isLoading={isLoading}>
              <div className="ml-card ml-chat-pane">
                {/* Header do Chat */}
                {!selectedConversation ? (
                  <div className="ml-chat-header">
                    <div className="ml-chat-header__placeholder">
                      Selecione uma conversa
                    </div>
                  </div>
                ) : (
                  <div className="ml-chat-header">
                    <div
                      className="ml-avatar"
                      style={{
                        background: activePalette.bg,
                        color: activePalette.text,
                        borderColor: activePalette.border,
                      }}
                    >
                      {initialsOf(selectedConversation.nome_comprador)}
                    </div>

                    <div className="ml-chat-header__title-wrap">
                      <div>
                        <div className="ml-chat-header__title">
                          {selectedConversation.nome_comprador}
                        </div>
                        <div className="ml-chat-header__sub">
                          <span
                            className="ml-badge"
                            style={{
                              background: activePalette.bg,
                              color: activePalette.text,
                            }}
                          >
                            Pós-Venda{' '}
                            {selectedConversation.id_pedido &&
                              `#${selectedConversation.id_pedido}`}
                          </span>
                        </div>
                      </div>

                      <OverlayTrigger
                        overlay={<Tooltip>Sincronizar mensagens</Tooltip>}
                      >
                        <button
                          className="ml-refresh-btn"
                          onClick={() =>
                            syncMessages(selectedConversation?.pack_id)
                          }
                          disabled={isSyncLoding}
                        >
                          {isSyncLoding ? (
                            <CustomSpinner
                              size={16}
                              color="white"
                            />
                          ) : (
                            <RefreshCcw size={16} />
                          )}
                        </button>
                      </OverlayTrigger>
                    </div>
                  </div>
                )}

                {/* Lista de Mensagens do Chat */}
                {selectedConversation?.messages?.length === 0 ? (
                  <div
                    className="ml-messages-scroll"
                    style={{ padding: '20px 20px 8px' }}
                  >
                    <div className="ml-messages-empty">
                      <p>Nenhuma mensagem encontrada nesta conversa</p>
                    </div>
                  </div>
                ) : (
                  <ContentLoading isLoading={isMessagesLoading}>
                    <div className="ml-messages-scroll">
                      {selectedConversation?.messages?.map((msg) => {
                        const isMine =
                          msg.isMine ||
                          String(msg.message_de) ===
                            String(selectedConversation?.seller_id) ||
                          String(msg.message_de) ===
                            String(localStorage.idconta)

                        const time = msg.data_criacao
                          ? formatDistanceToNow(
                              parseServerDate(msg.data_criacao),
                              { locale },
                            )
                          : ''

                        return (
                          <div
                            key={msg.id || msg.mensagem_id}
                            className={`ml-bubble-row ${
                              isMine
                                ? 'ml-bubble-row--mine'
                                : 'ml-bubble-row--theirs'
                            }`}
                          >
                            <div
                              className={`ml-bubble ${
                                isMine ? 'ml-bubble--mine' : 'ml-bubble--theirs'
                              }`}
                            >
                              {!isMine && (
                                <div className="ml-bubble__author">
                                  Comprador ({msg.message_de})
                                </div>
                              )}

                              {msg.anexo_url &&
                                (isImageUrl(msg.anexo_url) ? (
                                  <button
                                    onClick={() =>
                                      setLightboxImage(msg.anexo_url)
                                    }
                                  >
                                    <img
                                      src={msg.anexo_url}
                                      className="ml-bubble__image"
                                      alt="Anexo"
                                    />
                                  </button>
                                ) : (
                                  <button
                                    className="ml-file-attachment"
                                    onClick={() =>
                                      downloadFile(
                                        msg.anexo_url,
                                        getFileNameFromUrl(msg.anexo_url),
                                      )
                                    }
                                    title="Clique para baixar"
                                  >
                                    <div className="ml-file-attachment__icon">
                                      <FileBox size={22} />
                                    </div>
                                    <div className="ml-file-attachment__info">
                                      <span className="ml-file-attachment__name">
                                        {getFileNameFromUrl(msg.anexo_url)}
                                      </span>
                                      <span className="ml-file-attachment__type">
                                        {getFileExtension(msg.anexo_url)
                                          ? getFileExtension(
                                              msg.anexo_url,
                                            ).toUpperCase()
                                          : 'ARQUIVO'}
                                      </span>
                                    </div>
                                  </button>
                                ))}

                              <div>{msg.mensagem}</div>

                              <div className="ml-bubble__meta">
                                <Clock size={10} />
                                {formatDate(
                                  msg.data_criacao,
                                  'dd/MM/yyyy hh:mm',
                                )}{' '}
                                ({time} atrás)
                              </div>
                            </div>
                          </div>
                        )
                      })}

                      <div ref={messagesEndRef} />
                    </div>
                  </ContentLoading>
                )}

                {/* Input de Resposta */}
                <div className="ml-input-bar">
                  <OverlayTrigger overlay={<Tooltip>Anexar arquivo</Tooltip>}>
                    <button
                      className="ml-refresh-btn"
                      onClick={() => inputFileRef.current?.click()}
                    >
                      <Plus size={16} />
                    </button>
                  </OverlayTrigger>

                  <input
                    ref={inputFileRef}
                    type="file"
                    accept=".jpg,.png,.pdf,.txt"
                    onChange={selectAttachment}
                    style={{ display: 'none' }}
                  />

                  {selectedAttachment?.name && (
                    <OverlayTrigger
                      overlay={<Tooltip>Dois cliques para remover</Tooltip>}
                    >
                      {!['image/jpeg', 'image/png'].includes(
                        selectedAttachment?.type,
                      ) ? (
                        <div
                          onDoubleClick={() => setSelectedAttachment(null)}
                          style={{
                            width: 30,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            background:
                              selectedAttachment?.type === 'application/pdf'
                                ? '#FF0000'
                                : '#00B2FF',
                            borderRadius: 10,
                          }}
                        >
                          {selectedAttachment?.type === 'application/pdf' ? (
                            <FilePdf
                              width={20}
                              height={20}
                              color="white"
                            />
                          ) : (
                            <FileTxt
                              width={25}
                              height={25}
                              color="white"
                            />
                          )}
                        </div>
                      ) : (
                        <div
                          onDoubleClick={() => setSelectedAttachment(null)}
                          style={{
                            width: 30,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={URL.createObjectURL(selectedAttachment)}
                            style={{
                              width: '100%',
                              height: '100%',
                            }}
                          />
                        </div>
                      )}
                    </OverlayTrigger>
                  )}

                  <input
                    type="text"
                    placeholder="Digite sua resposta para o comprador..."
                    className="ml-input-bar__input"
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (canSend) handleSendMessage()
                      }
                    }}
                  />

                  <button
                    className={`ml-input-bar__send${
                      canSend ? '' : ' ml-input-bar__send--disabled'
                    }`}
                    onClick={handleSendMessage}
                    disabled={!canSend}
                  >
                    <SendHorizontal size={16} />
                    Enviar
                  </button>
                </div>
              </div>
            </ContentLoading>
          </div>
        </ContentLoading>
      </div>

      {isAllSyncLoding && (
        <div className="ml-progress-overlay">
          <p>Sincronizando mensagens... ()</p>

          <div>
            <ProgressBar
              // animated
              now={progress}
              max={conversations.length}
              label={`${progress}%`}
            />
          </div>
        </div>
      )}

      {/* Lightbox / Modal de Imagem Ampliada */}
      {lightboxImage && (
        <div
          className="ml-lightbox-overlay"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="ml-lightbox-close"
            onClick={() => setLightboxImage(null)}
          >
            ✕
          </button>
          <img
            src={lightboxImage}
            className="ml-lightbox-image"
            onClick={(e) => e.stopPropagation()}
            alt="Anexo em visualização"
          />
        </div>
      )}
    </>
  )
}
